import { useMemo } from "react";

import Routes, { VenueRouteV2 } from "@greeter-guest/utility/Routes";
import { ImageAsset, Venue } from "@greeter/core";

import { BigContentCard } from "@greeter-guest/components/BigContentCard";
import { useLocation } from "react-router-dom";
import { isAfter } from "date-fns";
import { toPrettyDateString } from "@greeter-guest/utility/DateUtil";
import { DateFactory, dateFormatter } from "@greeter/date";
import { WeeklyOpeningHours } from "@greeter/core";
import { classNames } from "@greeter/util";
import { BaseProps, GreenLabel, GreyLabel, YellowLabel } from "@greeter/matter";

import "./VenueCard.scss";
import styles from "./VenueCard.module.scss";
import { useIonRouter } from "@ionic/react";
import { useDetectTab } from "@greeter-guest/hooks/index";

type VenueCardProps = BaseProps & {
  venue: Venue;
};

export function VenueLabel({ venue }: VenueCardProps) {
  const { openingHours, launchDate } = venue;

  const now = DateFactory.create();

  const isLaunchDateInTheFuture = isAfter(launchDate, now);

  if (isLaunchDateInTheFuture) {
    return <GreenLabel text={`Åbner d. ${toPrettyDateString(launchDate)}`} />;
  }

  const openStatus = WeeklyOpeningHours.getOpenStatus(openingHours, now);

  switch (openStatus?.when) {
    case "now":
      return <GreenLabel text="Åbent" />;
    case "today":
      return <YellowLabel text={`Åbner kl. ${openStatus.at.toString()}`} />;
    case "tomorrow":
      return (
        <YellowLabel text={`Åbner i morgen ${openStatus.at.toString()}`} />
      );
    case "future":
      return (
        <YellowLabel text={`Åbner d. ${dateFormatter.format(openStatus.at)}`} />
      );
    default:
      return <GreyLabel text="Lukket" />;
  }
}

export function VenueCard({ venue, style, className }: VenueCardProps) {
  const router = useIonRouter();
  const address = useMemo(
    () => `${venue.address.street}, ${venue.address.city}`,
    [venue.address.city, venue.address.street]
  );
  const label = useMemo(() => <VenueLabel venue={venue} />, [venue]);
  const tab = useDetectTab();

  return (
    <BigContentCard
      {...classNames(styles.BigContentCard, className)}
      onClick={() =>
        router.push(
          VenueRouteV2.get(tab ?? Routes.venuesTab.tabName).route({
            venueId: venue.id,
          }),
          "forward",
          "push"
        )
      }
      title={venue.name}
      cover={ImageAsset.findUriWithSizeOrDefault(
        venue.coverAsset,
        "16x9-w1024"
      )}
      venue={venue}
      description={address}
      label={label}
      style={style}
    />
  );
}
